import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Typography from '@mui/material/Typography';
import { useNavigate } from '@tanstack/react-location';
import { useCallback, useEffect, useState } from 'react';
import { apiRawFetch } from '../../main/client';
import SpinnerIcon from '../../main/component/icon/SpinnerIcon';

interface Property {
    id: number;
    name: string;
    minNights: number;
    created: string;
    updated: string;
}

interface PropertySelectionDialogProps {
    open: boolean;
    targetPath: string;
}

const PropertySelectionDialog = ({
    open,
    targetPath,
}: PropertySelectionDialogProps) => {
    const [properties, setProperties] = useState<Property[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const navigate = useNavigate();

    const handlePropertySelected = useCallback(
        (propertyId: number) => {
            const path = targetPath || '/home';
            const destination = `/${propertyId}${path}`;
            navigate({ to: destination });
        },
        [navigate, targetPath]
    );

    useEffect(() => {
        if (!open) {
            return;
        }

        let isMounted = true;

        const fetchProperties = async () => {
            try {
                setLoading(true);
                setError(null);
                const response = await apiRawFetch('api/properties');

                if (!response.ok) {
                    throw new Error('Failed to fetch properties');
                }

                const data = await response.json();

                if (!isMounted) return;

                setProperties(data);

                // Auto-select if only one property
                if (data.length === 1) {
                    // Small delay to ensure the component is fully mounted
                    setTimeout(() => {
                        if (isMounted) {
                            const path = targetPath || '/home';
                            const destination = `/${data[0].id}${path}`;
                            navigate({ to: destination });
                        }
                    }, 100);
                }
            } catch (err) {
                if (!isMounted) return;
                setError(err instanceof Error ? err.message : 'Unknown error');
            } finally {
                if (isMounted) {
                    setLoading(false);
                }
            }
        };

        fetchProperties();

        return () => {
            isMounted = false;
        };
    }, [open, navigate, targetPath]);

    return (
        <Dialog
            open={open}
            maxWidth="sm"
            fullWidth
            disableEscapeKeyDown
            PaperProps={{
                sx: {
                    borderRadius: 0,
                    border: '4px solid #6366f1',
                    backgroundColor: '#1a1a2e',
                },
            }}
        >
            <DialogTitle
                sx={{
                    borderBottom: '3px solid #6366f1',
                    backgroundColor: '#0f0f23',
                    fontWeight: 900,
                    fontSize: '1.5rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    py: 2.5,
                }}
            >
                Select Property
            </DialogTitle>
            <DialogContent
                sx={{
                    backgroundColor: '#1a1a2e',
                    p: 0,
                }}
            >
                <Box sx={{ py: 3, px: 3, minHeight: 200 }}>
                    {loading && (
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                minHeight: 200,
                            }}
                        >
                            <SpinnerIcon />
                        </Box>
                    )}

                    {error && (
                        <Alert
                            severity="error"
                            sx={{
                                borderRadius: 0,
                                border: '3px solid #ef4444',
                                backgroundColor: 'rgba(239, 68, 68, 0.1)',
                                fontWeight: 600,
                                '& .MuiAlert-icon': {
                                    color: '#ef4444',
                                },
                            }}
                        >
                            {error}
                        </Alert>
                    )}

                    {!loading && !error && properties.length === 0 && (
                        <Alert
                            severity="warning"
                            sx={{
                                borderRadius: 0,
                                border: '3px solid #f59e0b',
                                backgroundColor: 'rgba(245, 158, 11, 0.1)',
                                fontWeight: 600,
                                '& .MuiAlert-icon': {
                                    color: '#f59e0b',
                                },
                            }}
                        >
                            No properties available. Contact administrator.
                        </Alert>
                    )}

                    {!loading && !error && properties.length > 1 && (
                        <>
                            <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{
                                    mb: 2,
                                    fontWeight: 600,
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.05em',
                                }}
                            >
                                Select a property to continue
                            </Typography>
                            <List
                                sx={{
                                    border: '2px solid #374151',
                                    borderRadius: 0,
                                }}
                            >
                                {properties.map((property, index) => (
                                    <ListItemButton
                                        key={property.id}
                                        onClick={() =>
                                            handlePropertySelected(property.id)
                                        }
                                        sx={{
                                            borderBottom:
                                                index < properties.length - 1
                                                    ? '1px solid #374151'
                                                    : 'none',
                                            borderLeft: '4px solid transparent',
                                            transition: 'all 0.2s ease',
                                            fontWeight: 600,
                                            py: 2,
                                            '&:hover': {
                                                borderLeftColor: '#6366f1',
                                                backgroundColor:
                                                    'rgba(99, 102, 241, 0.1)',
                                                transform: 'translateX(4px)',
                                            },
                                        }}
                                    >
                                        <ListItemText
                                            primary={property.name}
                                            primaryTypographyProps={{
                                                fontWeight: 700,
                                                fontSize: '1rem',
                                                textTransform: 'uppercase',
                                                letterSpacing: '0.05em',
                                            }}
                                            secondary={`ID: ${property.id}`}
                                            secondaryTypographyProps={{
                                                fontWeight: 600,
                                                fontSize: '0.875rem',
                                            }}
                                        />
                                    </ListItemButton>
                                ))}
                            </List>
                        </>
                    )}
                </Box>
            </DialogContent>
            {!loading && !error && properties.length === 0 && (
                <DialogActions
                    sx={{
                        borderTop: '3px solid #374151',
                        backgroundColor: '#0f0f23',
                        p: 3,
                        gap: 1.5,
                        justifyContent: 'flex-end',
                    }}
                >
                    <Button
                        onClick={() => navigate({ to: '/' })}
                        variant="outlined"
                        sx={{
                            borderRadius: 0,
                            borderWidth: '2px',
                            borderColor: '#374151',
                            fontWeight: 700,
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em',
                            px: 3,
                            py: 1,
                            '&:hover': {
                                borderWidth: '2px',
                                borderColor: '#374151',
                                backgroundColor: 'rgba(55, 65, 81, 0.1)',
                            },
                        }}
                    >
                        Go to Home
                    </Button>
                </DialogActions>
            )}
        </Dialog>
    );
};

export default PropertySelectionDialog;
